import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Transition } from 'react-transition-group'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import { DataContext } from '@aytm/app/stats/page/sidebar/coding/CodingPanel'

import { collator, encodeText } from '@aytm/app/stats/lib/util/helpers'

import {
  CODEBOOK_MIN_LENGTH,
  CODEBOOK_MAX_LENGTH,
  TAG_MIN_LENGTH,
  TAG_MAX_LENGTH
} from '@aytm/app/stats/page/components/lib/constants'
import { FORBIDDEN_CODEBOOK_SYMBOLS, FORBIDDEN_TAG_SYMBOLS } from '@aytm/app/stats/page/sidebar/coding//lib/constants'

import { DEFAULT_SELECTED_QUESTION } from '@aytm/app/stats/page/comments/lib/constants'

import useSingleAndDoubleClick from '@aytm/app/stats/page/components/hooks/useSingleAndDoubleClick'

import { Button } from 'primereact/button'

import CodingTag from '@aytm/app/stats/page/sidebar/coding/CodingTag'
import InputNew from '@aytm/app/stats/page/sidebar/coding/components/InputNew'
import InputUpdate from '@aytm/app/stats/page/sidebar/coding/components/InputUpdate'
import CodebookSettings from '@aytm/app/stats/page/sidebar/coding/components/CodebookSettings'

import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

let dndTimer = null

const CodingCodebook = ({
  codebook,
  error,
  newItem,
  newItems,
  editingItem,
  processingTag,
  settingsItem,
  collapsedRefs,
  isEditing,
  isProcessing,
  isCurrentProcessing,
  isNewProcessing,
  onDNDStart,
  onDNDEnd
}) => {
  const {
    root,
    mode,
    features,
    tagsList,
    tagsCodebooks,
    selectedQuestion,
    isSkipperDisabled,
    onCollapse,
    onError,
    onCodebookConfirm,
    onTagConfirm,
    onTagsConfirm,
    onCloseDropdown,
    onCancelEditing,
    onToggleNew
  } = useContext(DataContext)

  const [collapsed, setCollapsed] = useState(
    Object.hasOwn(collapsedRefs.current, codebook.codebook) ? collapsedRefs.current[codebook.codebook] : true
  )

  const [transitionDuration, setTransitionDuration] = useState(500)

  const [dndDroppedTag, setDndDroppedTag] = useState(null)

  const dndComponentRef = useRef(null)
  const dndItemRef = useRef(null)

  const componentRef = useRef(null)

  const collapsedRef = useRef()
  collapsedRef.current = collapsed

  const inputValueRef = useRef(codebook.codebook)

  const isDragging = useRef(false)

  const isOpened = useMemo(
    () =>
      (settingsItem &&
        settingsItem.item == codebook.codebook &&
        settingsItem.type == codebook.type &&
        settingsItem.codebook === undefined) ||
      false,
    [codebook, settingsItem]
  )

  const isDNDDisabled = useMemo(
    () => codebook.disabled || !['comments', 'video-responses'].includes(mode) || features.oe_tags_management.disabled,
    [codebook.disabled, mode, features.oe_tags_management.disabled]
  )

  const tags = useMemo(() => {
    const _tags = codebook.tags.reduce((acc, cur) => {
      const _tag = tagsList.find(tag => `${codebook.codebook}: ${cur}` == tag.tag && codebook.type == tag.type)
      return _tag && null !== _tag ? acc.concat(_tag) : acc
    }, [])
    _tags.sort((a, b) => collator.compare(a.label, b.label))
    return _tags
  }, [codebook, tagsList])

  const onDNDStartTag = () => {
    isDragging.current = true
    dispatchEvent(new CustomEvent('dndStartTag'))
  }
  const onDNDEndTag = () => {
    dndTimer && clearTimeout(dndTimer)
    dndTimer = setTimeout(() => {
      isDragging.current = false
    }, 300)
    dispatchEvent(new CustomEvent('dndEndTag'))
  }

  const onDNDEnter = useCallback(() => {
    if (isDragging.current) return
    dndComponentRef.current.classList.add(style['component-active'])
  }, [isDragging])
  const onDNDLeave = useCallback(() => {
    if (isDragging.current) return
    dndComponentRef.current.classList.remove(style['component-active'])
  }, [isDragging])

  const onDNDDrop = useCallback(
    data => {
      if (isDragging.current) return

      const item = data.item || data.data
      if (null === item || (item.windowUID == PageGlobals.WINDOW_UID && item.codebook == codebook.codebook)) return

      const newValue = `${codebook.codebook}: ${item.label}`

      setCollapsed(false)

      if (item.windowUID == PageGlobals.WINDOW_UID) {
        if (tagsList.some(tag => newValue.toLowerCase() == tag.tag.toLowerCase())) {
          onCloseDropdown()
          onCancelEditing()

          setDndDroppedTag(item)
        } else {
          onTagConfirm('clone', {
            ...item,
            destination: {
              codebook: codebook.codebook,
              type: codebook.type
            }
          })
        }
      } else {
        onTagConfirm('create', {
          tag: newValue,
          type: codebook.type,
          label: item.label,
          value: newValue,
          codebook: codebook.codebook
        })
      }

      setCollapsed(false)
    },
    [codebook, tagsList, onTagConfirm, onCloseDropdown, onCancelEditing]
  )

  const onDNDStartCodebook = () => {
    onDNDStart(dndItemRef.current)
    !isSkipperDisabled &&
      !features.ai_assistants_oe_coding.disabled &&
      dispatchEvent(new CustomEvent('dndStartCodebook', { detail: { guided: !codebook.tags.length } }))
  }
  const onDNDEndCodebook = () => {
    onDNDEnd()
    !isSkipperDisabled && !features.ai_assistants_oe_coding.disabled && dispatchEvent(new CustomEvent('dndEndCodebook'))
  }

  const fetchData = useCallback(() => {
    return { ...codebook, windowUID: PageGlobals.WINDOW_UID }
  }, [codebook])

  const dndComponent =
    isEditing || isProcessing || features.oe_tags_management.disabled
      ? null
      : ADND.targets
          .data(
            {
              acceptTypes: ['sidebar-tag'],
              dynamicPosition: true
            },
            { capture: false }
          )
          .onEnter(onDNDEnter)
          .onLeave(onDNDLeave)
          .onDrop(onDNDDrop)

  const dndItem =
    selectedQuestion == DEFAULT_SELECTED_QUESTION || isEditing || isProcessing || isDNDDisabled
      ? null
      : ADND.sources
          .data({
            type: 'sidebar-codebook',
            clearDragImage: false
          })
          .onStart(onDNDStartCodebook)
          .onEnd(onDNDEndCodebook)
          .fetchData(fetchData)

  const onToggle = () => {
    setCollapsed(!collapsedRef.current)
    setTransitionDuration(500)
  }

  const onActionConfirm = useCallback(
    action => {
      const _inputValue = inputValueRef.current.trim()
      const newValue = encodeText(_inputValue, false)

      if ('update' == action && newValue == codebook.codebook) {
        onCloseDropdown()
        onCancelEditing()
      } else if (
        'update' == action &&
        (_inputValue.length < CODEBOOK_MIN_LENGTH || _inputValue.length > CODEBOOK_MAX_LENGTH)
      ) {
        onError({
          codebook: codebook.codebook,
          name: 'updateError',
          message: `A codebook length should be between ${CODEBOOK_MIN_LENGTH} and ${CODEBOOK_MAX_LENGTH} characters`
        })
      } else if (
        'update' == action &&
        tagsCodebooks.some(item => newValue.toLowerCase() == item.codebook.toLowerCase())
      ) {
        onError({
          codebook: codebook.codebook,
          name: 'updateError',
          message: 'A codebook with the same name already exists'
        })
      } else {
        onCodebookConfirm(action, codebook, newValue)
      }
    },
    [codebook, tagsCodebooks, onError, onCodebookConfirm, onCloseDropdown, onCancelEditing]
  )

  const onMergeTag = useCallback(async () => {
    await onTagConfirm(
      'merge',
      {
        ...dndDroppedTag,
        destination: {
          codebook: codebook.codebook,
          type: codebook.type
        }
      },
      dndDroppedTag.update || dndDroppedTag.label
    )
    setDndDroppedTag(null)
  }, [codebook, dndDroppedTag, onTagConfirm])

  const onAddTag = useCallback(
    (value, tags) => {
      if (Array.isArray(tags)) {
        const newTags = (
          tagsCodebooks.some(item => value.trim().toLowerCase() == item.codebook.toLowerCase())
            ? tags
            : [value, ...tags]
        )
          .filter(
            tag =>
              tag.trim().length >= TAG_MIN_LENGTH &&
              tag.trim().length <= TAG_MAX_LENGTH &&
              !tagsList.some(
                item => `${codebook.codebook}: ${encodeText(tag.trim(), false)}`.toLowerCase() == item.tag.toLowerCase()
              )
          )
          .map(tag => {
            const newValue = `${codebook.codebook}: ${encodeText(tag.trim(), false)}`

            return {
              tag: newValue,
              type: 'manual',
              label: encodeText(tag.trim(), false),
              value: newValue,
              codebook: codebook.codebook
            }
          })

        if (0 < newTags.length) {
          onTagsConfirm('create', newTags, codebook)
        } else {
          onError({
            codebook: codebook.codebook,
            name: 'createError',
            message: 'No codes to create'
          })
        }
      } else {
        const _value = value.trim()
        const newValue = `${codebook.codebook}: ${encodeText(_value, false)}`

        if (_value.length < TAG_MIN_LENGTH || _value.length > TAG_MAX_LENGTH) {
          onError({
            codebook: codebook.codebook,
            tag: newValue,
            name: 'createError',
            message: `A code length should be between ${TAG_MIN_LENGTH} and ${TAG_MAX_LENGTH} characters`
          })
        } else if (tagsList.some(item => newValue.toLowerCase() == item.tag.toLowerCase())) {
          onError({
            codebook: codebook.codebook,
            tag: newValue,
            name: 'createError',
            message: 'A code with the same name already exists'
          })
        } else {
          onTagConfirm('create', {
            tag: newValue,
            type: 'manual',
            label: encodeText(_value, false),
            value: newValue,
            codebook: codebook.codebook
          })
        }
      }
    },
    [codebook, tagsList, tagsCodebooks, onError, onTagConfirm, onTagsConfirm]
  )

  const addCommentsSearchOption = useCallback(
    () => dispatchEvent(new CustomEvent('addCommentsSearchOption', { detail: { item: codebook } })),
    [codebook]
  )

  const onTitleClick = useSingleAndDoubleClick(
    isEditing || isNewProcessing ? null : onToggle,
    isProcessing ? null : addCommentsSearchOption
  )

  useEffect(() => {
    if (dndComponent) dndComponent.connect(dndComponentRef.current)

    return () => {
      if (dndComponent) dndComponent.disconnect()
    }
  }, [dndComponent])

  useEffect(() => {
    if (dndItem) dndItem.connect(dndItemRef.current)

    return () => {
      if (dndItem) dndItem.disconnect()
    }
  }, [dndItem])

  useEffect(
    () => tags.some(tag => newItems.some(newItem => newItem.tag == tag.tag)) && setCollapsed(false),
    [tags, newItems]
  )

  useEffect(() => isNewProcessing && dndItemRef.current.scrollIntoView(false), [isNewProcessing])

  useEffect(() => {
    setTransitionDuration(collapsed ? 500 : 10)

    onCollapse(codebook.codebook, collapsed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, codebook.codebook])

  const renderHeader = () => (
    <div
      className={classNames(style['codebook-header'], {
        [style['codebook-header-collapsed']]: collapsed
      })}
    >
      <div
        ref={dndItemRef}
        data-testid={`stat-coding-codebook-title-${codebook.codebook}`}
        className={classNames(style['codebook-subheader'], {
          [style['draggable']]:
            selectedQuestion != DEFAULT_SELECTED_QUESTION && !isEditing && !isProcessing && !isDNDDisabled
        })}
        onClick={onTitleClick}
      >
        <span className={style['codebook-header-icon']}>
          <i
            className={collapsed ? 'icon-aytmfd-folder-closed' : 'icon-aytmfd-folder-opened'}
            style={{ color: codebook.color }}
          />
        </span>
        <span className={style['codebook-header-title']}>
          <InputUpdate
            ariaLabel="Codebook name"
            root={root}
            value={codebook.codebook}
            popupText={
              0 < codebook.stat.length
                ? `used in ${codebook.stat.map(q => `<b>${q.id.toUpperCase()}</b> (${q.amount})`).join(', ')}`
                : ''
            }
            maxLength={CODEBOOK_MAX_LENGTH}
            forbiddenSymbols={FORBIDDEN_CODEBOOK_SYMBOLS}
            isEditing={isEditing}
            isProcessing={isCurrentProcessing}
            onChange={inputValue => (inputValueRef.current = inputValue)}
            onConfirm={() => onActionConfirm('update')}
            onCancel={onCancelEditing}
          />
        </span>
      </div>

      {isEditing || isNewProcessing ? null : (
        <span
          data-testid={`stat-coding-codebook-toggle-${codebook.codebook}`}
          className={style['codebook-header-toggle']}
          aria-label="Show codes"
          aria-expanded={!collapsed}
          {...pressable(onToggle)}
        >
          <i className="icon-aytmfd-chevron-bracket" />
        </span>
      )}

      {features.oe_tags_management.disabled ? null : (
        <CodebookSettings
          codebookRef={dndItemRef}
          codebook={codebook}
          opened={isOpened}
          isEditing={isEditing}
          isProcessing={isProcessing}
          isCurrentProcessing={isCurrentProcessing}
          onConfirm={onActionConfirm}
        />
      )}
    </div>
  )

  return (
    <div
      ref={dndComponentRef}
      className={classNames(style.codebook, {
        [style['codebook-editting']]: isEditing,
        [style['codebook-disabled']]: isNewProcessing,
        [style['blink']]: isNewProcessing
      })}
    >
      {renderHeader()}

      {error &&
        error.message &&
        'createError' != error.name &&
        codebook.codebook == error.codebook &&
        !Object.hasOwn(error, 'tag') && <div className={style['component-error']}>{error.message}</div>}

      <Transition in={!collapsed} timeout={transitionDuration}>
        {status => (
          <div
            className={`${style['transition-panel']}${status ? ' ' + style['transition-panel-' + status] : ''}`}
            style={{ transitionDuration: `${transitionDuration}ms` }}
          >
            <div ref={componentRef} className={style['codebook-body']}>
              {0 < tags.length ? (
                <div className={style['codebook-items']}>
                  {tags.map((tag, index) => (
                    <CodingTag
                      key={`tag-${tag.type}-${tag.tag}-${index + 1}`}
                      tag={tag}
                      error={error}
                      settingsItem={settingsItem}
                      isEditing={editingItem && editingItem.tag == tag.tag}
                      isProcessing={isProcessing}
                      isCurrentProcessing={
                        isProcessing && processingTag.current && processingTag.current.tag == tag.tag
                      }
                      isNewProcessing={newItems.some(newItem => newItem.tag == tag.tag)}
                      onDNDStart={onDNDStartTag}
                      onDNDEnd={onDNDEndTag}
                      setDndDroppedTag={setDndDroppedTag}
                    />
                  ))}
                </div>
              ) : null}

              {null !== dndDroppedTag ? (
                <div className={style['component-merge']}>
                  <div className={style['component-note']}>A code with the same name already exists</div>
                  <div className={style['component-merge-buttons']}>
                    <Button
                      data-testid={`stat-coding-codebook-dnd-cancel-${codebook.codebook}`}
                      className="p-button-rounded p-button-blue p-button-secondary"
                      label="Cancel"
                      disabled={isProcessing}
                      onClick={isProcessing ? null : () => setDndDroppedTag(null)}
                    />
                    <Button
                      data-testid={`stat-coding-codebook-merge-${codebook.codebook}`}
                      className="p-button-rounded p-button-blue"
                      label="Merge codes"
                      loading={isProcessing}
                      disabled={isProcessing}
                      onClick={isProcessing ? null : () => onMergeTag()}
                    />
                  </div>
                </div>
              ) : null}

              {codebook.disabled || features.oe_tags_management.disabled ? null : (
                <InputNew
                  placeholder="New code"
                  maxLength={TAG_MAX_LENGTH}
                  forbiddenSymbols={FORBIDDEN_TAG_SYMBOLS}
                  error={
                    error &&
                    error.message &&
                    'createError' == error.name &&
                    codebook.codebook == error.codebook &&
                    Object.hasOwn(error, 'tag')
                      ? error
                      : null
                  }
                  multiline
                  isAdding={`${codebook.codebook} - tag` == newItem}
                  isProcessing={isProcessing}
                  onAdd={onAddTag}
                  onToggle={value => onToggleNew(value ? `${codebook.codebook} - tag` : null)}
                />
              )}
            </div>
          </div>
        )}
      </Transition>
    </div>
  )
}

CodingCodebook.propTypes = {
  codebook: PropTypes.object,
  error: PropTypes.object,
  newItem: PropTypes.string,
  newItems: PropTypes.array,
  editingItem: PropTypes.object,
  processingTag: PropTypes.object,
  settingsItem: PropTypes.object,
  collapsedRefs: PropTypes.any,
  isEditing: PropTypes.bool,
  isProcessing: PropTypes.bool,
  isCurrentProcessing: PropTypes.bool,
  isNewProcessing: PropTypes.bool,
  onSentimentsChange: PropTypes.func,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func
}

CodingCodebook.displayName = 'CodingCodebook'

export default CodingCodebook
